/**
 * "Top Rated" / "Most Rated" sorting for the collection and search pages.
 *
 * Shopify cannot sort by a metafield, so this works in two steps:
 *  1. It reads a very small JSON feed of EVERY product in the current (filtered) collection / search
 *     (templates/collection.ratings.liquid, templates/search.ratings.liquid: id, rating, rating count, grid page,
 *     handle), so the ordering is global with no product limit. The feed is cached (localStorage) and is
 *     pre-loaded when the shopper shows intent, so sorting is usually instant.
 *  2. It renders the product cards in that order, 120 per page with numbered pagination (1, 2, 3 ...).
 *     Cards are fetched 20 at a time ("list mode": ?sort_by=cv-list:<handles>, see sections/main-collection.liquid),
 *     i.e. 6 parallel requests per page, and shown as skeletons until they arrive. The next page is pre-fetched in
 *     the background, and fetched cards / sorted lists are kept in memory, so moving between numbered pages is
 *     instant. If list mode is unavailable, whole grid pages are fetched instead.
 * Products with an empty rating are placed last.
 *
 * The same feed also carries the product tags written as "1 > 2 > 3 > 4 > 5". Part 2 is the "Collection" filter and the
 * parts after it are the "Sub-collection" filter (two dropdowns next to the Top Rated / Most Rated buttons). Choosing
 * one filters the feed in the browser and shows the matching products with the same numbered pages.
 *
 * State lives in the URL: sort_by=rating-desc|rating-count-desc, cv_col=<collection>, cv_sub=<sub-collection> and
 * page=<numbered page>.
 */

// ---- settings you may want to change -------------------------------------------------------------
const PAGE_SIZE = 48; // cards per numbered page (same as the collection grid: 48 per page)
const FEED_CONCURRENCY = 16; // parallel ratings-feed requests when the shopper is waiting for them
const FEED_BACKGROUND_CONCURRENCY = 6; // ...and while they are only pre-loaded quietly in the background
const FEED_PAGE_SIZE = 250; // products per feed request (Shopify's maximum for {% paginate %}: 5x fewer requests than 50)
/** Products per server page of the grid: 120 on collections, 40 on search (data-page-size, see snippets/product-grid). */
const gridPageSize = () => Number(document.querySelector('ul.product-grid[data-page-size]')?.getAttribute('data-page-size')) || 40;
const CARD_CONCURRENCY = 12; // parallel card requests
const LIST_SIZE = 20; // cards per list-mode request (Shopify allows 20 all_products lookups per render)
const CACHE_TTL = 30 * 60 * 1000; // after this the stored feed is still used at once, and refreshed in the background
const CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // ...and dropped after a week
const WARM_MAX_PRODUCTS = 30000; // pre-load the feed in the background for results up to this size
// -------------------------------------------------------------------------------------------------

const PARAM_COL = 'cv_col'; // URL parameter of the Collection filter (part 2 of the tag)
const PARAM_SUB = 'cv_sub'; // URL parameter of the Sub-collection filter (the parts after it)

const SORTS = {
  'rating-desc': (a, b) => hasValue(b.v) - hasValue(a.v) || b.v - a.v || b.c - a.c || a.idx - b.idx,
  'rating-count-desc': (a, b) => hasValue(b.c) - hasValue(a.c) || b.c - a.c || b.v - a.v || a.idx - b.idx,
};

const hasValue = (n) => (n > 0 ? 1 : 0);

/**
 * The feed needs many requests on big result sets, so it is kept in localStorage. A stored feed is used at once even
 * when it is older than CACHE_TTL (ratings change slowly); it is then refreshed in the background.
 */
function readStoredDataset(key) {
  try {
    const raw = localStorage.getItem(`cv-ratings6:${key}`);
    if (!raw) return null;
    const { time, words, rows } = JSON.parse(raw);
    if (!Array.isArray(rows) || Array.isArray(words) === false || Date.now() - time > CACHE_MAX_AGE) return null;
    const size = gridPageSize();
    return {
      stale: Date.now() - time > CACHE_TTL,
      rows: rows.map(([id, v, c, h, tags], idx) => ({
        id,
        v,
        c,
        h,
        idx,
        page: Math.floor(idx / size) + 1,
        t: tags.map((path) => path.map((n) => words[n])),
      })),
    };
  } catch {
    return null;
  }
}

function storeDataset(key, rows) {
  // Tag names repeat across thousands of products: store each distinct name once.
  const words = [];
  const index = new Map();
  const wordId = (word) => {
    if (!index.has(word)) index.set(word, words.push(word) - 1);
    return index.get(word);
  };
  const packed = rows.map((r) => [r.id, r.v, r.c, r.h, r.t.map((path) => path.map(wordId))]);
  const value = JSON.stringify({ time: Date.now(), words, rows: packed });
  try {
    localStorage.setItem(`cv-ratings6:${key}`, value);
  } catch {
    // Storage is full (every filter combination has its own entry): clear the old feeds and try once more.
    try {
      Object.keys(localStorage)
        .filter((name) => name.startsWith('cv-ratings'))
        .forEach((name) => localStorage.removeItem(name));
      localStorage.setItem(`cv-ratings6:${key}`, value);
    } catch {
      /* storage unavailable: the in-memory cache still works */
    }
  }
}
const datasetCache = new Map();
const datasetsReady = new Map(); // base url -> rows (synchronous access once loaded)
let applyToken = 0;

/* ------------------------------------------------------------------ helpers */

function getView() {
  const p = new URL(window.location.href).searchParams;
  return {
    sort: p.get('sort_by') || '',
    page: Math.max(parseInt(p.get('page') || '1', 10) || 1, 1),
    col: p.get(PARAM_COL) || '',
    sub: p.get(PARAM_SUB) || '',
  };
}

/*
  Translated pages (Google Translate): a Collection / Sub-collection choice reloads the page with Shopify's own tag
  filter (filter.p.tag = every product tag of that choice), so the server renders the matching products and Google
  Translate translates all of them like any normal page. Needs the "Tag" filter in Search & Discovery > Filters
  (data-cv-server-tags); without it the filters keep working in the browser as before.
*/
const TAG_PARAM = 'filter.p.tag';
const serverFiltered = () => {
  if (!new URL(window.location.href).searchParams.has(TAG_PARAM)) return false;
  const grid = document.querySelector('ul.product-grid[data-cv-all]');
  // Same count as the whole collection: the "Tag" filter is not set up in Search & Discovery, so Shopify ignored it.
  if (grid && grid.getAttribute('data-cv-count') === grid.getAttribute('data-cv-all')) {
    try {
      sessionStorage.setItem('cv-no-tagfilter', '1');
    } catch {
      /* ignore */
    }
    return false;
  }
  return true;
};
const tagFilterMissing = () => {
  try {
    return sessionStorage.getItem('cv-no-tagfilter') === '1';
  } catch {
    return false;
  }
};

function serverTagsFor(col, sub) {
  if (!document.querySelector('[data-cv-server-tags]') || tagFilterMissing()) return null;
  let tags;
  try {
    tags = JSON.parse(document.querySelector('script[data-cv-tagmap]')?.textContent || 'null');
  } catch {
    return null;
  }
  if (!Array.isArray(tags)) return null;
  const c = norm(col);
  const s = norm(sub);
  return tags.filter((tag) => {
    const parts = String(tag).split('>').map((part) => part.trim());
    if (parts.length < 2) return false;
    if (c && norm(parts[1]) !== c) return false;
    if (s && (parts.length < 3 || norm(parts[parts.length - 1]) !== s)) return false;
    return true;
  });
}

/** Our own view replaces the server's grid when a rating sort or a Collection / Sub-collection filter is chosen. */
const isActive = (view) =>
  Boolean(SORTS[view.sort]) || (!serverFiltered() && (Boolean(view.col) || Boolean(view.sub)));

/** URL of the collection / search with the shopper's native filters, without our own sort / paging params. */
function getBaseUrl() {
  const url = new URL(window.location.href);
  ['page', 'view', 'section_id', PARAM_COL, PARAM_SUB, TAG_PARAM].forEach((k) => url.searchParams.delete(k));
  if (SORTS[url.searchParams.get('sort_by') || '']) url.searchParams.delete('sort_by');
  url.hash = '';
  return url;
}

function getGrid() {
  return document.querySelector('ul.product-grid[data-last-page]');
}

async function runLimited(tasks, limit) {
  const queue = [...tasks];
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length) await queue.shift()();
  });
  await Promise.all(workers);
}

/** Runs async tasks with a concurrency limit; foreground tasks always go before background (prefetch) ones. */
function createPool(limit) {
  let active = 0;
  const high = [];
  const low = [];
  const pump = () => {
    while (active < limit && (high.length || low.length)) {
      const job = (high.length ? high : low).shift();
      active++;
      job
        .task()
        .then(job.resolve, job.reject)
        .finally(() => {
          active--;
          pump();
        });
    }
  };
  return (task, background = false) =>
    new Promise((resolve, reject) => {
      (background ? low : high).push({ task, resolve, reject });
      pump();
    });
}
const cardPool = createPool(CARD_CONCURRENCY);

/* ------------------------------------------------------------------ data */

async function fetchFeedPage(baseUrl, page) {
  const url = new URL(baseUrl);
  url.searchParams.set('view', 'ratings');
  url.searchParams.set('page', String(page));
  let lastError;
  for (let attempt = 0; attempt < 6; attempt++) {
    let wait = 400 * 2 ** attempt;
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (response.ok) return await response.json();
      lastError = new Error(`Feed page ${page} failed (${response.status})`);
      if (response.status !== 429 && response.status < 500) break;
      if (response.status === 429 || response.status === 503) {
        // The store is throttling us: halve the number of parallel requests for the rest of the visit.
        feedPool.throttled = true;
        feedPool.limit = Math.max(2, Math.floor(feedPool.limit / 2));
        wait = Math.max(wait, (Number(response.headers.get('Retry-After')) || 0) * 1000);
      }
    } catch (error) {
      lastError = error;
    }
    await new Promise((resolve) => setTimeout(resolve, Math.min(wait, 8000)));
  }
  throw lastError;
}

/** Feed requests share one pool: quiet (few at a time) until a shopper shows intent, then wide open. */
const feedPool = { limit: FEED_BACKGROUND_CONCURRENCY, active: 0, queue: [], throttled: false };

function pumpFeedPool() {
  while (feedPool.active < feedPool.limit && feedPool.queue.length) {
    const job = feedPool.queue.shift();
    feedPool.active++;
    job
      .task()
      .then(job.resolve, job.reject)
      .finally(() => {
        feedPool.active--;
        pumpFeedPool();
      });
  }
}

function boostFeedPool() {
  if (feedPool.throttled || feedPool.limit >= FEED_CONCURRENCY) return;
  feedPool.limit = FEED_CONCURRENCY;
  pumpFeedPool();
}

const runFeedTask = (task) =>
  new Promise((resolve, reject) => {
    feedPool.queue.push({ task, resolve, reject });
    pumpFeedPool();
  });

/** Downloads the whole feed (all pages in parallel) and returns the rows in the default order. */
async function downloadFeed(baseUrl, report, partial) {
  // The grid already tells how many pages of 40 there are, so every request can start at once (no waiting for page 1).
  const gridPages = Number(getGrid()?.dataset.lastPage) || 0;
  const guess = gridPages ? Math.ceil((gridPages * gridPageSize()) / FEED_PAGE_SIZE) : 1;
  const results = [];
  let done = 0;
  let total = guess;
  let known = guess; // highest page number requested so far

  const load = async (page) => {
    const data = await fetchFeedPage(baseUrl, page);
    results[page - 1] = data.items || [];
    if (data.pages) total = Math.max(data.pages, 1);
    report(Math.min(++done / Math.max(total, known), 1));
    if (partial) {
      // The first pages arrived in order: their products are the first results of any filter (default order).
      let ready = 0;
      while (ready < results.length && results[ready]) ready++;
      if (ready > shown) {
        shown = ready;
        partial(() => feedRows(results, ready));
      }
    }
    return data;
  };
  let shown = 0;

  await Promise.all(Array.from({ length: guess }, (_, i) => runFeedTask(() => load(i + 1))));

  // The guess was too low (products were added meanwhile): fetch what is missing.
  if (total > guess) {
    known = total;
    await Promise.all(Array.from({ length: total - guess }, (_, i) => runFeedTask(() => load(guess + 1 + i))));
  }

  return feedRows(results, total);
}

/** Feed pages -> rows in the default order (the first `count` pages). */
function feedRows(results, count) {
  const size = gridPageSize();
  const seen = new Set();
  const rows = [];
  for (const items of results.slice(0, count)) {
    for (const row of items || []) {
      const id = String(row[0]);
      if (seen.has(id)) continue; // a page past the end can repeat the last one
      seen.add(id);
      const idx = rows.length;
      rows.push({
        id,
        v: Number(row[1]) || 0,
        c: Number(row[2]) || 0,
        page: Math.floor(idx / size) + 1, // the grid page (of 40) that renders this product
        h: String(row[4] || ''),
        // Tag paths without their first part: [collection, sub-collection, sub-collection, ...]
        t: Array.isArray(row[5])
          ? row[5].map((path) => (Array.isArray(path) ? path.map((part) => String(part).trim()).filter(Boolean) : [])).filter((path) => path.length)
          : [],
        idx,
      });
    }
  }
  return rows;
}

/** Progress of the ratings feed per result set, shared by everyone waiting for it (warm-up, sort click). */
const feedProgress = new Map(); // key -> { ratio, listeners }

function feedState(key) {
  if (!feedProgress.has(key)) feedProgress.set(key, { ratio: 0, listeners: new Set(), partials: new Set(), prefix: null });
  return feedProgress.get(key);
}

/** Every product of the collection / search (all pages), in the default order. */
async function loadDataset(baseUrl, onProgress, urgent = Boolean(onProgress)) {
  const key = baseUrl.toString();
  const state = feedState(key);
  if (onProgress) {
    state.listeners.add(onProgress);
    onProgress(state.ratio);
  }
  const report = (ratio) => {
    state.ratio = ratio;
    state.listeners.forEach((listener) => listener(ratio));
  };
  if (!datasetCache.has(key)) {
    datasetCache.set(
      key,
      (async () => {
        const stored = readStoredDataset(key);
        if (stored) {
          report(1);
          if (stored.stale) {
            // Use the stored feed right now; fetch a fresh one quietly for the next visit.
            downloadFeed(baseUrl, () => {})
              .then((rows) => rows.length && storeDataset(key, rows))
              .catch(() => {});
          }
          return stored.rows;
        }
        const rows = await downloadFeed(baseUrl, report, (getRows) => {
          state.prefix = getRows;
          state.partials.forEach((listener) => listener(getRows));
        });
        storeDataset(key, rows);
        return rows;
      })()
        .then((rows) => {
          datasetsReady.set(key, rows);
          syncFilterUi(); // the Collection / Sub-collection options come from the feed
          return rows;
        })
        .catch((error) => {
          datasetCache.delete(key);
          feedProgress.delete(key);
          throw error;
        })
    );
  }
  if (urgent) boostFeedPool(); // somebody is waiting for the feed (sort click / hover): use every connection
  const promise = datasetCache.get(key);
  if (onProgress) {
    const done = () => state.listeners.delete(onProgress);
    promise.then(done, done);
  }
  return promise;
}

/* ------------------------------------------------------------------ UI */

/** "Vitamins · 42%" on the dropdown / tab the shopper just used, while the filter or sort is being prepared. */
/** A small "42%" badge drawn on top of a filter / tab (visible on phones too, where menu text is cut short). */
function setBadge(host, text) {
  if (!host) return;
  let badge = host.querySelector(':scope > .cv-busy-badge');
  if (text == null) {
    badge?.remove();
    return;
  }
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'cv-busy-badge notranslate';
    badge.setAttribute('translate', 'no');
    badge.setAttribute('aria-hidden', 'true');
    host.classList.add('cv-busy-host');
    host.append(badge);
  }
  badge.textContent = text;
}

function controlBusy(percent) {
  // A small "42%" badge on the dropdown / tab itself. It sits on top of the control, so it stays visible on phones,
  // where the dropdown text is cut short and changes to an option's text are not shown.
  const control = window.__cvUsedControl;
  if (!(control instanceof Element) || !control.isConnected) return;
  const host = control instanceof HTMLSelectElement ? control.parentElement : control;
  if (!host) return;
  let badge = host.querySelector(':scope > .cv-busy-badge');
  if (percent == null) {
    badge?.remove();
    control.removeAttribute('data-cv-loading');
    return;
  }
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'cv-busy-badge notranslate';
    badge.setAttribute('translate', 'no');
    badge.setAttribute('aria-hidden', 'true');
    host.classList.add('cv-busy-host');
    host.append(badge);
  }
  badge.textContent = `${percent}%`;
  control.setAttribute('data-cv-loading', '');
}

function setStatus(grid, text) {
  let status = document.querySelector('.metafield-sort-status');
  if (!text) {
    controlBusy(null);
    status?.remove();
    return;
  }
  if (!status) {
    status = document.createElement('div');
    status.className = 'metafield-sort-status';
    status.setAttribute('role', 'status');
    grid.parentElement?.insertBefore(status, grid);
  }
  status.textContent = text;
}

/** "Sorting products… 45%" (or "Filtering products…") with a progress bar (ratio 0..1) above the grid. */
function setProgress(grid, ratio, noun = 'Sorting products') {
  let status = document.querySelector('.metafield-sort-status');
  if (!status || !status.querySelector('.metafield-sort-bar')) {
    status?.remove();
    status = document.createElement('div');
    status.className = 'metafield-sort-status';
    status.setAttribute('role', 'status');
    status.innerHTML = '<span class="metafield-sort-label"></span><span class="metafield-sort-bar"><i></i></span>';
    grid.parentElement?.insertBefore(status, grid);
  }
  const percent = Math.max(0, Math.min(100, Math.round(ratio * 100)));
  const label = status.querySelector('.metafield-sort-label');
  const bar = status.querySelector('.metafield-sort-bar i');
  if (label) label.textContent = `${noun}… ${percent}%`;
  controlBusy(percent);
  if (bar instanceof HTMLElement) bar.style.width = `${percent}%`;
}

/** Keeps the sorting menu showing the right selected option (the server renders its default order). */
function syncSortUi(sortBy) {
  if (!SORTS[sortBy]) return;
  document.querySelectorAll('input[name="sort_by"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const selected = input.value === sortBy;
    input.checked = selected;
    const option = input.closest('[role="option"]');
    if (option) {
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      option.setAttribute('tabindex', selected ? '0' : '-1');
    }
    if (selected) {
      document.querySelectorAll('sorting-filter-component .facets__status').forEach((el) => {
        el.textContent = input.dataset.optionName || '';
      });
    }
  });
  document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
    if (select instanceof HTMLSelectElement) select.value = sortBy;
  });
}

/** The server-rendered numbered pagination belongs to the default order: hidden while a rating sort is active. */
function serverNavs() {
  return document.querySelectorAll('nav.pagination:not([data-cv-nav])');
}

function removeOwnNav() {
  document.querySelectorAll('nav.pagination[data-cv-nav]').forEach((el) => el.remove());
}

function pageUrl(page) {
  const url = new URL(window.location.href);
  if (page > 1) url.searchParams.set('page', String(page));
  else url.searchParams.delete('page');
  url.hash = '';
  return url;
}

/** Same markup and classes as snippets/batch-pagination.liquid, so it picks up the theme's pagination styles. */
function renderNav(grid, current, total, arrows) {
  removeOwnNav();
  if (total < 2) return;

  const nav = document.createElement('nav');
  nav.className = 'pagination pagination--batch';
  nav.setAttribute('data-cv-nav', '');
  nav.setAttribute('aria-label', 'Pagination');
  const list = document.createElement('ul');
  list.className = 'pagination__list';
  list.setAttribute('role', 'list');

  const item = (className = '') => {
    const li = document.createElement('li');
    li.className = `pagination__item ${className}`.trim();
    list.append(li);
    return li;
  };
  const link = (li, label, html, page, extra = '') => {
    const el = document.createElement(page ? 'a' : 'span');
    el.className = `pagination__link ${extra}`.trim();
    el.setAttribute('aria-label', label);
    el.innerHTML = html;
    if (page) {
      el.setAttribute('href', pageUrl(page).toString());
      el.setAttribute('data-cv-page', String(page));
    }
    li.append(el);
    return el;
  };

  const prev = link(item(), 'Previous page', arrows.left, current > 1 ? current - 1 : 0, 'pagination__link--arrow');
  if (current <= 1) {
    prev.classList.add('pagination__link--disabled');
    prev.setAttribute('aria-disabled', 'true');
  }

  let gap = false;
  for (let n = 1; n <= total; n++) {
    if (n === 1 || n === total || Math.abs(n - current) <= 1) {
      gap = false;
      const el = link(item(), `Page ${n}`, String(n), n === current ? 0 : n, 'pagination__link--page');
      if (n === current) {
        el.classList.add('pagination__link--current');
        el.setAttribute('aria-current', 'page');
        el.removeAttribute('aria-label');
      }
    } else if (!gap) {
      gap = true;
      link(item('pagination__item--gap'), '', '&hellip;', 0, 'pagination__link--gap').setAttribute('aria-hidden', 'true');
    }
  }

  const next = link(item(), 'Next page', arrows.right, current < total ? current + 1 : 0, 'pagination__link--arrow');
  if (current >= total) {
    next.classList.add('pagination__link--disabled');
    next.setAttribute('aria-disabled', 'true');
  }

  nav.append(list);
  nav.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-cv-page]') : null;
    if (!target) return;
    // With a translated page (Google Translate), load the page normally so the new products are translated too.
    if (/(?:^|;\s*)googtrans=\/[^/;]+\/(?!en(?:;|$))[^;]+/.test(document.cookie)) return;
    event.preventDefault();
    history.pushState('', '', pageUrl(Number(target.getAttribute('data-cv-page'))).toString());
    apply();
    (document.querySelector('.facets-controls-wrapper') || grid).scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  grid.after(nav);
}

/* ------------------------------------------------------------------ main */

/** Chevron icons of the server-rendered nav (kept so the sorted view looks identical). */
let arrowIcons = { left: '&lsaquo;', right: '&rsaquo;' };
function rememberArrows() {
  const navs = document.querySelectorAll('nav.pagination:not([data-cv-nav])');
  const nav = navs[0];
  if (!nav) return;
  const left = nav.querySelector('li:first-child svg')?.outerHTML;
  const right = nav.querySelector('li:last-child svg')?.outerHTML;
  if (left && right) arrowIcons = { left, right };
}

/* ---- per-result-set caches: fetched cards and sorted lists survive page changes ---- */

const resultCaches = new Map();

function getResultCache(key) {
  if (!resultCaches.has(key)) {
    if (resultCaches.size >= 4) resultCaches.delete(resultCaches.keys().next().value);
    resultCaches.set(key, {
      nodes: new Map(), // product id -> card <li>
      inflight: new Map(), // "f:id" / "b:id" -> Promise
      pages: new Map(), // grid page -> Promise (page mode)
      sorted: new Map(), // sort name -> sorted rows
      // List mode: 20 cards per request. Falls back to whole grid pages when the server does not answer it.
      mode: sessionStorage.getItem('cv-list-off') ? 'page' : 'list',
      listOk: 0,
      listFails: 0,
    });
  }
  return resultCaches.get(key);
}

function cacheNode(cache, li) {
  const id = li.dataset.productId;
  if (id && !cache.nodes.has(id)) cache.nodes.set(id, li.ownerDocument === document ? li : document.adoptNode(li));
}

async function fetchItems(url) {
  let lastError;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (response.ok) {
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(doc.querySelectorAll('.product-grid__item'));
      }
      lastError = new Error(`Request failed (${response.status})`);
      if (response.status !== 429 && response.status < 500) break;
    } catch (error) {
      lastError = error;
    }
    await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt)); // throttled: wait, then try again
  }
  throw lastError;
}


function ensureGridPage(cache, ctx, serverPage) {
  if (!cache.pages.has(serverPage)) {
    const url = new URL(ctx.baseUrl);
    url.searchParams.set('page', String(serverPage));
    url.searchParams.set('view', 'cards'); // cards only: skips the slow filter block
    if (ctx.sectionId) url.searchParams.set('section_id', ctx.sectionId);
    const promise = fetchItems(url).then((lis) => lis.forEach((li) => cacheNode(cache, li)));
    promise.catch(() => cache.pages.delete(serverPage));
    cache.pages.set(serverPage, promise);
  }
  return cache.pages.get(serverPage);
}

function listUrl(ctx, handles) {
  const url = new URL(ctx.baseUrl.origin + ctx.baseUrl.pathname);
  // The search page needs its query; collections do not need their filters (the handles are explicit).
  if (url.pathname.startsWith('/search')) {
    for (const [name, value] of ctx.baseUrl.searchParams) if (name === 'q' || name.startsWith('options[')) url.searchParams.set(name, value);
  }
  url.searchParams.set('sort_by', `cv-list:${handles.join(',')}`);
  url.searchParams.set('view', 'cards');
  if (ctx.sectionId) url.searchParams.set('section_id', ctx.sectionId);
  return url;
}

/** Fetches one group of up to LIST_SIZE products (list mode, with a whole-page fallback). */
async function loadGroup(cache, group, ctx, background) {
  if (!background && ctx.isStale()) return;

  if (cache.mode === 'list' && ctx.sectionId) {
    try {
      const handles = group.filter((entry) => entry.h).map((entry) => entry.h);
      if (handles.length === group.length) {
        (await fetchItems(listUrl(ctx, handles))).forEach((li) => cacheNode(cache, li));
        if (group.every((entry) => cache.nodes.has(entry.id))) {
          cache.listOk++;
          return;
        }
        cache.listFails++;
        if (cache.listOk === 0 && cache.listFails >= 2) {
          cache.mode = 'page';
          try {
            sessionStorage.setItem('cv-list-off', '1');
          } catch {
            /* ignore */
          }
        }
      }
    } catch (error) {
      console.error('Could not load product cards', error);
    }
  }

  // Fallback: the grid pages that hold the missing products.
  const pages = [...new Set(group.filter((entry) => !cache.nodes.has(entry.id)).map((entry) => entry.page))];
  await Promise.all(pages.map((page) => ensureGridPage(cache, ctx, page).catch((error) => console.error(error))));
}

/**
 * Loads the cards of `entries` (sorted rows) into cache.nodes: 20 per request, requests in parallel.
 * foreground = needed now; background = pre-fetch (goes after foreground requests in the queue).
 * @returns {Promise<void>} resolves when all requests settled
 */
function loadCards(cache, entries, ctx, background = false) {
  const waits = [];
  const missing = [];

  for (const entry of entries) {
    if (cache.nodes.has(entry.id)) continue;
    const running = cache.inflight.get(entry.id);
    if (running) waits.push(running);
    else missing.push(entry);
  }

  for (let i = 0; i < missing.length; i += LIST_SIZE) {
    const group = missing.slice(i, i + LIST_SIZE);
    const promise = cardPool(() => loadGroup(cache, group, ctx, background), background).finally(() =>
      group.forEach((entry) => cache.inflight.delete(entry.id))
    );
    group.forEach((entry) => cache.inflight.set(entry.id, promise));
    waits.push(promise);
  }

  return Promise.all(waits).then(() => undefined);
}

function makeSkeleton() {
  const li = document.createElement('li');
  li.className = 'product-grid__item cv-skeleton';
  li.setAttribute('aria-hidden', 'true');
  li.innerHTML =
    '<div class="cv-skeleton__img"></div><div class="cv-skeleton__line"></div><div class="cv-skeleton__line cv-skeleton__line--short"></div>';
  return li;
}

/** Quick "Top Rated" / "Most Rated" tabs next to the sort menu. */
function syncTabs(view = getView()) {
  document.querySelectorAll('[data-cv-tab]').forEach((tab) => {
    tab.setAttribute('aria-pressed', String(tab.getAttribute('data-cv-tab') === view.sort));
  });
}

/* ------------------------------------------------------------------ Collection / Sub-collection filters */

const norm = (value) => String(value).trim().toLowerCase();
const byIndex = (a, b) => a.idx - b.idx;

/** Does the product have a tag path whose Collection is `col` and (when given) a Sub-collection `sub`? */
function rowMatches(row, col, sub) {
  for (const path of row.t) {
    if (col && norm(path[0]) !== col) continue;
    // Sub-collection = the last part of the tag ("1 > 2 > … > last"), on its own or with a Collection.
    if (sub && (path.length < 2 || norm(path[path.length - 1]) !== sub)) continue;
    return true;
  }
  return false;
}

/** The sorted (and filtered) rows of a view, computed once per result set; paging only slices it. */
function listFor(cache, dataset, view) {
  const key = `${view.sort}|${view.col}|${view.sub}`;
  if (!cache.sorted.has(key)) {
    const col = norm(view.col);
    const sub = norm(view.sub);
    const rows = col || sub ? dataset.filter((row) => rowMatches(row, col, sub)) : [...dataset];
    cache.sorted.set(key, rows.sort(SORTS[view.sort] || byIndex));
  }
  return cache.sorted.get(key);
}

/** Distinct Collections (all products) and Sub-collections (of the chosen Collection), each with a product count. */
function collectOptions(rows, col) {
  const cols = new Map();
  const subs = new Map();
  const add = (map, name) => {
    const key = norm(name);
    if (!key) return false;
    const entry = map.get(key) || { label: name, n: 0 };
    entry.n++;
    map.set(key, entry);
    return true;
  };
  for (const row of rows) {
    const seenCols = new Set();
    const seenSubs = new Set();
    for (const path of row.t) {
      const colKey = norm(path[0]);
      if (colKey && !seenCols.has(colKey)) {
        seenCols.add(colKey);
        add(cols, path[0]);
      }
      if (col && colKey !== col) continue;
      if (path.length < 2) continue;
      const last = path[path.length - 1];
      const subKey = norm(last);
      if (subKey && !seenSubs.has(subKey)) {
        seenSubs.add(subKey);
        add(subs, last);
      }
    }
  }
  const sorted = (map) => [...map.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  return { cols: sorted(cols), subs: sorted(subs) };
}

const isTranslated = () => /(?:^|;\s*)googtrans=\/[^/;]+\/(?!en(?:;|$))[^;]+/.test(document.cookie);
const COUNT_RE = /\s*\(\s*[\d.,\s]+\)\s*$/;
const optionPools = new WeakMap(); // select -> Map(norm(value) -> the server-rendered <option>, which Google Translate translates)

/* The menus reuse their <option> nodes (server-rendered ones included); see btf-gridtranslate.js for translation. */
function fillSelect(select, options, current, placeholder) {
  let pool = optionPools.get(select);
  if (!pool) {
    pool = new Map();
    for (const o of select.options) if (o.value && !o.disabled) pool.set(norm(o.value), o);
    optionPools.set(select, pool);
    select.__cvPlaceholder = select.options[0] && !select.options[0].value ? select.options[0] : null;
  }
  const fragment = document.createDocumentFragment();
  fragment.append(select.__cvPlaceholder || new Option(placeholder, ''));
  let found = false;
  for (const option of options) {
    const key = norm(option.label);
    let node = pool.get(key);
    if (!node) {
      node = new Option(option.label, option.label);
      pool.set(key, node);
    }
    node.textContent = `${option.label} (${option.n})`; // translated pages: btf-gridtranslate.js translates the name
    fragment.append(node);
    if (current && key === norm(current)) found = true;
  }
  if (current && !found) fragment.append(pool.get(norm(current)) || new Option(current, current)); // a value from the URL that has no products here
  select.replaceChildren(fragment);
  select.value = current ? [...select.options].find((o) => norm(o.value) === norm(current))?.value ?? '' : '';
  select.classList.toggle('is-selected', Boolean(select.value));
  fitSelectToText(select);
}

/** Shrinks a dropdown pill to the end of its shown word (a native select is otherwise as wide as its longest option). */
function fitSelectToText(select) {
  const parent = select.parentElement;
  if (!parent) return;
  // Modern browsers do it in CSS (field-sizing: content, see snippets/sorting.liquid).
  if (window.CSS?.supports?.('field-sizing', 'content')) {
    select.style.removeProperty('width');
    return;
  }
  const probe = /** @type {HTMLSelectElement} */ (document.createElement('select'));
  probe.className = select.className;
  probe.setAttribute('aria-hidden', 'true');
  probe.tabIndex = -1;
  probe.style.cssText = 'position:absolute;visibility:hidden;width:auto;max-width:none;pointer-events:none;';
  probe.append(new Option(select.options[select.selectedIndex]?.textContent || '', ''));
  parent.append(probe);
  const max = parseFloat(getComputedStyle(select).maxWidth);
  const width = probe.offsetWidth;
  probe.remove();
  if (width > 0) select.style.width = `${Number.isFinite(max) ? Math.min(width, max) : width}px`;
}

/** "Collection · Loading 42%" in the dropdown itself while the feed downloads (not touched while a menu is open). */
let loadingHooked = '';
function hookLoadingLabel(key) {
  if (loadingHooked === key) return;
  loadingHooked = key;
  const state = feedState(key);
  const update = (ratio) => {
    const done = ratio >= 1 || datasetsReady.has(key);
    document.querySelectorAll('select[data-cv-filter]').forEach((select) => {
      // Once the shopper has used one dropdown, the progress shows on that one only.
      const used = window.__cvUsedFilter;
      const mine = !used || select.getAttribute('data-cv-filter') === used;
      setBadge(select.parentElement, mine && !done && ratio > 0 ? `${Math.round(ratio * 100)}%` : null);
    });
  };
  state.listeners.add(update);
  update(state.ratio);
}

/** Fills the two dropdowns from the loaded feed and shows the current choice (also after the section re-renders). */
function syncFilterUi() {
  const selects = document.querySelectorAll('select[data-cv-filter]');
  if (!selects.length) return;
  const view = getView();
  const key = getBaseUrl().toString();
  const rows = datasetsReady.get(key);
  if (!rows) {
    hookLoadingLabel(key);
    // Feed not loaded yet: show the current choice only; the options arrive when the feed is ready.
    selects.forEach((select) => {
      const current = select.getAttribute('data-cv-filter') === 'col' ? view.col : view.sub;
      if (!current || select.value) return;
      const match = [...select.options].find((o) => o.value && norm(o.value) === norm(current));
      if (match) select.value = match.value; // keep the server-rendered (translated) names
      else fillSelect(select, [], current, select.options[0]?.textContent || '');
    });
    return;
  }
  const { cols, subs } = collectOptions(rows, norm(view.col));
  selects.forEach((select) => {
    if (select.getAttribute('data-cv-filter') === 'col') fillSelect(select, cols, view.col, 'Collection');
    else fillSelect(select, subs, view.sub, 'Sub-collection');
  });
}

// Remember which dropdown the shopper is using, so the loading progress shows on that one.
['pointerdown', 'focusin', 'touchstart'].forEach((type) =>
  document.addEventListener(
    type,
    (event) => {
      const select = event.target instanceof Element ? event.target.closest('select[data-cv-filter]') : null;
      if (select) window.__cvUsedFilter = select.getAttribute('data-cv-filter');
      const control = event.target instanceof Element ? event.target.closest('select[data-cv-filter], [data-cv-tab]') : null;
      if (control) window.__cvUsedControl = control;
    },
    { passive: true, capture: true }
  )
);

function onFilterChange(select) {
  window.__cvUsedFilter = select.getAttribute('data-cv-filter');
  window.__cvUsedControl = select;
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('page');
  const set = (name, value) => (value ? url.searchParams.set(name, value) : url.searchParams.delete(name));
  if (select.getAttribute('data-cv-filter') === 'col') {
    set(PARAM_COL, select.value);
    url.searchParams.delete(PARAM_SUB); // the Sub-collections belong to the Collection
  } else {
    set(PARAM_SUB, select.value);
  }

  if (isTranslated() && !SORTS[url.searchParams.get('sort_by') || '']) {
    // Translated page: let the server filter (see serverTagsFor), so every product arrives translated.
    const col = url.searchParams.get(PARAM_COL) || '';
    const sub = url.searchParams.get(PARAM_SUB) || '';
    const tags = col || sub ? serverTagsFor(col, sub) : serverFiltered() ? [] : null;
    if (tags && (tags.length || !(col || sub)) && tags.length <= 150) {
      url.searchParams.delete(TAG_PARAM);
      tags.forEach((tag) => url.searchParams.append(TAG_PARAM, tag));
      setBadge(select.parentElement, '…');
      window.location.assign(url.toString());
      return;
    }
  }

  const stillCustom =
    SORTS[url.searchParams.get('sort_by') || ''] || url.searchParams.get(PARAM_COL) || url.searchParams.get(PARAM_SUB);
  if (!stillCustom) {
    // Back to the server's default grid (re-renders the section, no full page load).
    const facets = document.querySelector('facets-form-component');
    if (facets && typeof facets.updateFiltersByURL === 'function') facets.updateFiltersByURL(url.toString());
    else window.location.assign(url.toString());
    return;
  }
  history.pushState('', '', url.toString());
  syncFilterUi();
  apply();
}

function selectTab(sort) {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.hash = '';
  if (getView().sort === sort) {
    // Tab switched off.
    url.searchParams.delete('sort_by');
    if (url.searchParams.get(PARAM_COL) || url.searchParams.get(PARAM_SUB)) {
      // A Collection / Sub-collection filter is still on: keep our view, in the default order.
      history.pushState('', '', url.toString());
      apply();
      return;
    }
    // Back to the server's default order (re-renders the section, no full page load).
    const facets = document.querySelector('facets-form-component');
    if (facets && typeof facets.updateFiltersByURL === 'function') facets.updateFiltersByURL(url.toString());
    else window.location.assign(url.toString());
    return;
  }
  url.searchParams.set('sort_by', sort);
  history.pushState('', '', url.toString());
  apply();
}


/* ---- neighbour pages: prefetch cards + start their images so a page click is instant ---- */

let live = null; // { cache, ctx, list } of the page that is on screen
const warmedImages = [];
const warmedIds = new Set();

function warmCardImages(cache, entries) {
  for (const entry of entries) {
    if (warmedIds.has(entry.id)) continue;
    const li = cache.nodes.get(entry.id);
    const img = li && li.querySelector('img');
    if (!img) continue;
    warmedIds.add(entry.id);
    const warm = new Image();
    warm.decoding = 'async';
    warm.fetchPriority = 'low';
    const sizes = (img.getAttribute('sizes') || '').replace(/^\s*auto\s*,?\s*/, '');
    if (sizes) warm.sizes = sizes;
    if (img.getAttribute('srcset')) warm.srcset = img.getAttribute('srcset');
    warm.src = img.getAttribute('src') || '';
    warmedImages.push(warm);
  }
  if (warmedImages.length > 600) warmedImages.splice(0, warmedImages.length - 600);
}

function prefetchPage(page, withImages, background = true) {
  if (!live || page < 1) return Promise.resolve();
  const { cache, ctx, list } = live;
  const entries = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  if (!entries.length) return Promise.resolve();
  return loadCards(cache, entries, ctx, background).then(() => {
    if (withImages) warmCardImages(cache, entries);
  });
}

function prefetchAround(page) {
  if (navigator.connection && navigator.connection.saveData) return;
  const mine = live;
  setTimeout(async () => {
    // The two neighbours first, together (cards + images); then the one after next.
    await Promise.all([prefetchPage(page + 1, true), prefetchPage(page - 1, true)]);
    if (live !== mine) return;
    await prefetchPage(page + 2, false);
  }, 50);
}

function watchNavIntent() {
  const onIntent = (event) => {
    const link = event.target instanceof Element ? event.target.closest('nav[data-cv-nav] a[data-cv-page]') : null;
    if (!link) return;
    const page = Number(link.getAttribute('data-cv-page'));
    if (page) prefetchPage(page, true, false);
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
}

/*
  A filter chosen while the feed is still downloading: the feed pages that already arrived (in order) hold the first
  matching products, so they are shown as soon as there are enough for the page, instead of after the whole feed.
  The full result (count, page numbers) follows when the feed is complete; the cards are cached by then.
*/
function showEarly(grid, cache, ctx, view) {
  let done = false;
  const col = norm(view.col);
  const sub = norm(view.sub);
  return (getRows) => {
    if (done || ctx.isStale()) return;
    const rows = getRows();
    const list = rows.filter((row) => rowMatches(row, col, sub));
    if (list.length < view.page * PAGE_SIZE) return;
    done = true;
    const slice = list.slice((view.page - 1) * PAGE_SIZE, view.page * PAGE_SIZE);
    const fragment = document.createDocumentFragment();
    const pending = [];
    for (const entry of slice) {
      const node = cache.nodes.get(entry.id);
      if (node) {
        node.setAttribute('data-cv', '1');
        fragment.append(node);
      } else {
        const placeholder = makeSkeleton();
        fragment.append(placeholder);
        pending.push({ entry, placeholder });
      }
    }
    grid.replaceChildren(fragment);
    for (let i = 0; i < pending.length; i += LIST_SIZE) {
      const group = pending.slice(i, i + LIST_SIZE);
      loadCards(cache, group.map((item) => item.entry), ctx)
        .then(() => {
          if (ctx.isStale()) return;
          for (const { entry, placeholder } of group) {
            const node = cache.nodes.get(entry.id);
            if (node && placeholder.isConnected) {
              node.setAttribute('data-cv', '1');
              placeholder.replaceWith(node);
            }
          }
        })
        .catch(() => {});
    }
  };
}

let applying = null; // the view apply() is preparing right now

async function apply() {
  const grid = getGrid();
  if (!grid) return;
  const view = getView();
  syncTabs(view);

  // Left the rating sort (e.g. picked a native sort): bring back the server pagination.
  if (!isActive(view)) {
    live = null;
    applyToken++;
    setStatus(grid, '');
    removeOwnNav();
    serverNavs().forEach((el) => el.removeAttribute('hidden'));
    delete grid.dataset.cvMarker;
    syncFilterUi();
    return;
  }

  const marker = window.location.search;
  if (grid.dataset.cvMarker === marker) return;
  // Already preparing this exact view: the skeletons / early results it puts in the grid must not restart it.
  if (applying && applying.grid === grid && applying.marker === marker) return;
  const token = ++applyToken;
  const run = { grid, marker };
  applying = run;
  const isStale = () => token !== applyToken || !grid.isConnected;
  rememberArrows();

  const baseUrl = getBaseUrl();
  const key = baseUrl.toString();
  const cache = getResultCache(key);
  grid.querySelectorAll(':scope > .product-grid__item').forEach((li) => cacheNode(cache, li));
  const sectionId =
    document.querySelector('results-list')?.getAttribute('section-id') ||
    (grid.querySelector(':scope > .product-grid__item')?.id || '').replace(/-\d+$/, '');
  const ctx = { baseUrl, sectionId, isStale };

  try {
    // 1. The ratings of every product (instant when already loaded / cached).
    let dataset = datasetsReady.get(key);
    let base = 0; // share of the progress bar already used by the ratings feed
    if (!dataset) {
      // Instant feedback: the grid turns into placeholder cards at once (the real cards are already cached above).
      const placeholders = document.createDocumentFragment();
      for (let i = 0; i < PAGE_SIZE; i++) placeholders.append(makeSkeleton());
      grid.replaceChildren(placeholders);
      const label = SORTS[view.sort] ? 'Sorting products' : 'Filtering products';
      setProgress(grid, 0, label);
      const state = feedState(key);
      const early = !SORTS[view.sort] && (view.col || view.sub) ? showEarly(grid, cache, ctx, view) : null;
      if (early) {
        state.partials.add(early);
        if (state.prefix) early(state.prefix);
      }
      try {
        dataset = await loadDataset(baseUrl, (ratio) => {
          if (!isStale()) setProgress(grid, ratio * 0.5, label);
        });
      } finally {
        if (early) state.partials.delete(early);
      }
      base = 0.5;
    }
    if (isStale()) return;

    // 2. The sorted list is computed once per result set and sort; paging only slices it.
    const list = listFor(cache, dataset, view);
    live = { cache, ctx, list };
    const totalPages = Math.max(Math.ceil(list.length / PAGE_SIZE), 1);
    const page = Math.min(view.page, totalPages);
    if (page !== view.page) history.replaceState('', '', pageUrl(page).toString());
    const slice = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // 3. Show the page right away: known cards immediately, skeletons for the rest.
    const fragment = document.createDocumentFragment();
    const pending = [];
    for (const entry of slice) {
      const node = cache.nodes.get(entry.id);
      if (node) {
        node.setAttribute('data-cv', '1');
        fragment.append(node);
      } else {
        const placeholder = makeSkeleton();
        fragment.append(placeholder);
        pending.push({ entry, placeholder });
      }
    }
    grid.replaceChildren(fragment);
    grid.dataset.cvMarker = marker;
    serverNavs().forEach((el) => el.setAttribute('hidden', ''));
    renderNav(grid, page, totalPages, arrowIcons);
    syncSortUi(view.sort);
    syncFilterUi();
    if (!list.length) setStatus(grid, 'No products match these filters.');
    else if (pending.length && base > 0) setProgress(grid, base); // the feed was just downloaded: keep the bar going
    else setStatus(grid, ''); // changing page only needs cards: the placeholders are the feedback
    if (pending.length && base === 0) controlBusy(0); // the data was ready: the badge follows the product cards

    // 4. Fill the skeletons in sorted order as the cards arrive (20 per request, all requests in parallel).
    const groups = [];
    for (let i = 0; i < pending.length; i += LIST_SIZE) groups.push(pending.slice(i, i + LIST_SIZE));
    let loaded = 0;
    await Promise.all(
      groups.map(async (group) => {
        await loadCards(cache, group.map((item) => item.entry), ctx);
        if (isStale()) return;
        loaded += group.length;
        if (base > 0) setProgress(grid, base + (1 - base) * (loaded / pending.length));
        else controlBusy(Math.round((loaded / pending.length) * 100));
        for (const { entry, placeholder } of group) {
          const node = cache.nodes.get(entry.id);
          if (node && placeholder.isConnected) {
            node.setAttribute('data-cv', '1');
            placeholder.replaceWith(node);
          } else if (!node) {
            placeholder.remove();
          }
        }
      })
    );
    if (isStale()) return;
    setStatus(grid, '');

    // 5. Pre-fetch the neighbouring numbered pages (cards + images) so that clicking one is instant.
    prefetchAround(page);
  } catch (error) {
    console.error('Metafield sort failed', error);
    grid.classList.remove('is-metafield-sorting');
    setStatus(grid, 'Sorting is unavailable right now.');
    setTimeout(() => setStatus(grid, ''), 3000);
  } finally {
    if (applying === run) applying = null;
  }
}

let timer;
const schedule = () => {
  clearTimeout(timer);
  timer = setTimeout(apply, 120);
};

/** Starts downloading the ratings feed as soon as the shopper shows interest in sorting. */
let warmedKey = '';
function warmDataset(urgent = false) {
  const grid = getGrid();
  if (!grid) return Promise.resolve(null);
  const baseUrl = getBaseUrl();
  const key = baseUrl.toString();
  const promise = loadDataset(baseUrl, undefined, urgent);
  if (warmedKey !== key) {
    warmedKey = key;
    promise.catch(() => {
      warmedKey = '';
    });
  }
  return promise;
}

/** The shopper is about to pick a rating sort (hover / touch): load its feed and the cards of its first page. */
const warmedSorts = new Set();
async function warmSort(sort, urgent = true) {
  const grid = getGrid();
  if (!grid || !SORTS[sort]) return;
  const baseUrl = getBaseUrl();
  const marker = `${baseUrl}|${sort}`;
  if (warmedSorts.has(marker)) return;
  warmedSorts.add(marker);
  try {
    const dataset = await warmDataset(urgent);
    const cache = getResultCache(baseUrl.toString());
    const sectionId = document.querySelector('results-list')?.getAttribute('section-id') || '';
    const ctx = { baseUrl, sectionId, isStale: () => false };
    const sorted = listFor(cache, dataset, { sort, col: '', sub: '' });
    await loadCards(cache, sorted.slice(0, PAGE_SIZE), ctx, true);
    await loadCards(cache, sorted.slice(PAGE_SIZE, PAGE_SIZE * 2), ctx, true); // so that "page 2" is ready too
  } catch {
    warmedSorts.delete(marker);
  }
}

function watchSortInteraction() {
  const handler = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const tab = target.closest('[data-cv-tab]');
    if (tab) {
      warmSort(tab.getAttribute('data-cv-tab') || '');
      return;
    }
    if (!target.closest('sorting-filter-component, [data-cv-tabs], [data-cv-tagfilters]')) return;
    const option = target.closest('[role="option"]')?.querySelector('input[name="sort_by"]');
    if (option instanceof HTMLInputElement && SORTS[option.value]) warmSort(option.value);
    else warmDataset(true).catch(() => {});
  };
  ['pointerover', 'touchstart', 'focusin'].forEach((type) =>
    document.addEventListener(type, handler, { passive: true, capture: true })
  );

  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element ? event.target.closest('select[data-cv-filter]') : null;
    if (select instanceof HTMLSelectElement) onFilterChange(select);
  });

  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-cv-tab]') : null;
    if (!tab) return;
    event.preventDefault();
    selectTab(tab.getAttribute('data-cv-tab') || '');
  });
}

/** Only re-run when the product grid itself changed, not on every DOM change of the page. */
function touchesProductGrid(records) {
  for (const record of records) {
    const target = record.target;
    if (!(target instanceof Element)) continue;
    if (target.closest('ul.product-grid') || target.matches('results-list, #ResultsList')) return true;
    for (const node of record.addedNodes) {
      if (node instanceof Element && (node.matches('ul.product-grid, #ResultsList') || node.querySelector?.('ul.product-grid'))) {
        return true;
      }
    }
  }
  return false;
}

if (!window.__metafieldSortInit) {
  window.__metafieldSortInit = true;
  // Re-run whenever the product grid is re-rendered (filters, sorting, back/forward navigation).
  const root = document.querySelector('results-list') || document.body;
  new MutationObserver((records) => {
    if (touchesProductGrid(records)) schedule();
  }).observe(root, { childList: true, subtree: true });
  window.addEventListener('popstate', schedule);
  // facets.js changed only the sort (no server render needed): the URL already holds the new sort.
  document.addEventListener('cv:urlchange', schedule);
  document.addEventListener('facets:rendered', syncFilterUi);
  // Old copies of the feed (earlier formats) are no longer used.
  try {
    Object.keys(localStorage)
      .filter((name) => /^cv-ratings[45]:/.test(name))
      .forEach((name) => localStorage.removeItem(name));
  } catch {
    /* ignore */
  }
  watchSortInteraction();
  watchNavIntent();
  schedule();

  // No automatic download: the feed starts when the shopper reaches for a filter / sort (watchSortInteraction).
}
